import * as THREE from 'three'
import type { FloorLayout } from './types'
import {
  CORRIDOR_HALF_WIDTH,
  CORRIDOR_MARGIN,
  ENTRY_ZONE,
  SLOT_WIDTH,
  SOFFIT_HEIGHT,
  VAULT_HALF_WIDTH,
  VAULT_RISE,
} from './mallLayout'
import {
  MALL,
  makeBandStoneTexture,
  makeCurtainWallTexture,
  makeGatewaySignTexture,
  makeLightPoolTexture,
  makeLimestoneTexture,
  makeSkylightTexture,
  makeSoffitTexture,
  makeWingGraphicTexture,
  makeTerrazzoTexture,
  makeVerticalFalloffTexture,
} from './mallTextures'

/**
 * The landlord's shell: everything a tenant doesn't build. Floor finishes,
 * ceiling soffits, the glazed barrel vault over the concourse, the end curtain
 * wall, and the entrance gateway.
 *
 * The lighting model is deliberately mostly painted. Real concourses are lit by
 * a daylit roof plus dozens of recessed downlights; putting dozens of real
 * three.js lights in a scene that holds every floor at once would be ruinous, so
 * the vault, coves and downlight pools are emissive geometry, and the scene
 * carries only a hemisphere light, one sun, and an environment map. The result
 * is a bright interior at a flat, constant shader cost.
 */

/** Shared texture cache — one set of finishes for the whole mall, as built. */
export class MallFinishes {
  readonly terrazzo = makeTerrazzoTexture()
  readonly bandStone = makeBandStoneTexture()
  readonly limestone = makeLimestoneTexture()
  readonly soffit = makeSoffitTexture()
  readonly curtainWall = makeCurtainWallTexture()
  readonly lightPool = makeLightPoolTexture()
  readonly falloff = makeVerticalFalloffTexture()

  private disposables: THREE.Texture[] = []

  /** A repeat-tuned clone; the source texture keeps its own repeat for other users. */
  tiled(source: THREE.Texture, repeatX: number, repeatY: number): THREE.Texture {
    const tex = source.clone()
    tex.needsUpdate = true
    tex.wrapS = THREE.RepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(repeatX, repeatY)
    this.disposables.push(tex)
    return tex
  }

  track<T extends THREE.Texture>(tex: T): T {
    this.disposables.push(tex)
    return tex
  }

  dispose() {
    this.disposables.forEach((t) => t.dispose())
    this.disposables.length = 0
    ;[this.terrazzo, this.bandStone, this.limestone, this.soffit, this.curtainWall, this.lightPool, this.falloff]
      .forEach((t) => t.dispose())
  }
}

const HW = CORRIDOR_HALF_WIDTH

/** Z extent of a floor's shell: entrance apron in front, end wall behind. */
function extents(floor: FloorLayout) {
  const zNear = ENTRY_ZONE
  const zFar = -floor.corridorLength
  return { zNear, zFar, length: zNear - zFar, center: (zNear + zFar) / 2 }
}

export function buildFloorShell(floor: FloorLayout, fin: MallFinishes): THREE.Group {
  const group = new THREE.Group()
  const { zFar, length, center } = extents(floor)
  const x0 = floor.originX
  const accent = new THREE.Color(floor.color)
  const bays = Math.max(1, Math.round((floor.corridorLength - CORRIDOR_MARGIN) / SLOT_WIDTH))

  buildFloorFinishes(group, floor, fin, { x0, center, length, accent, bays })
  buildCeiling(group, fin, { x0, center, length, bays })
  buildSideWalls(group, fin, { x0, bayStart: -CORRIDOR_MARGIN, bayEnd: -CORRIDOR_MARGIN - bays * SLOT_WIDTH, zFar })
  buildEndWall(group, fin, { x0, zFar })
  buildEntrance(group, floor, fin)

  return group
}

// ── Floor finishes ─────────────────────────────────────────────────────

interface ShellCtx {
  x0: number
  center: number
  length: number
  accent: THREE.Color
  bays: number
}

function buildFloorFinishes(g: THREE.Group, floor: FloorLayout, fin: MallFinishes, ctx: ShellCtx) {
  const { x0, center, length, accent, bays } = ctx

  // Polished terrazzo field. Low roughness + the scene environment map is what
  // gives the concourse its wet sheen — the single most "mall" cue in the frame.
  const field = new THREE.Mesh(
    new THREE.PlaneGeometry(HW * 2, length),
    new THREE.MeshStandardMaterial({
      map: fin.tiled(fin.terrazzo, HW * 2 / 3.2, length / 3.2),
      roughness: 0.24,
      metalness: 0.02,
      envMapIntensity: 0.55,
    })
  )
  field.rotation.x = -Math.PI / 2
  field.position.set(x0, 0, center)
  g.add(field)

  // Honed stone border framing the shopfronts on both sides
  const bandW = 1.6
  const bandMat = new THREE.MeshStandardMaterial({
    map: fin.tiled(fin.bandStone, bandW / 1.2, length / 1.2),
    roughness: 0.55,
    metalness: 0.0,
    envMapIntensity: 0.4,
  })
  for (const s of [-1, 1]) {
    const band = new THREE.Mesh(new THREE.PlaneGeometry(bandW, length), bandMat)
    band.rotation.x = -Math.PI / 2
    band.position.set(x0 + s * (HW - bandW / 2), 0.002, center)
    g.add(band)
  }

  // Dark granite runners either side of the walking line. Real concourses use a
  // contrasting inlay like this to lead the eye down the length of the mall.
  const runnerMat = new THREE.MeshStandardMaterial({
    color: MALL.granite,
    roughness: 0.14,
    metalness: 0.2,
    envMapIntensity: 1.1,
  })
  for (const s of [-1, 1]) {
    const runner = new THREE.Mesh(new THREE.PlaneGeometry(0.34, length), runnerMat)
    runner.rotation.x = -Math.PI / 2
    runner.position.set(x0 + s * 2.1, 0.003, center)
    g.add(runner)
  }

  // Accent inlay across the floor at each bay line — the wing's wayfinding color
  const inlayMat = new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0.22 })
  for (let i = 0; i <= bays; i++) {
    const z = -CORRIDOR_MARGIN - i * SLOT_WIDTH
    const inlay = new THREE.Mesh(new THREE.PlaneGeometry(HW * 2 - 3.4, 0.1), inlayMat)
    inlay.rotation.x = -Math.PI / 2
    inlay.position.set(x0, 0.004, z)
    g.add(inlay)
  }

  // Downlight pools. Paired with the emissive fittings on the soffit above, they
  // read as the recessed 3000 K downlights that light a real concourse.
  const poolMat = new THREE.MeshBasicMaterial({
    map: fin.lightPool,
    color: MALL.warmLight,
    transparent: true,
    opacity: 0.2,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  })
  const poolGeo = new THREE.PlaneGeometry(5.4, 5.4)
  for (let i = 0; i < bays; i++) {
    const z = -CORRIDOR_MARGIN - i * SLOT_WIDTH - SLOT_WIDTH / 2
    for (const s of [-1, 1]) {
      const pool = new THREE.Mesh(poolGeo, poolMat)
      pool.rotation.x = -Math.PI / 2
      pool.position.set(x0 + s * 3.4, 0.02, z)
      g.add(pool)
    }
  }

  // The vault's own daylight, pooled down the centreline
  const daylightPool = new THREE.Mesh(
    new THREE.PlaneGeometry(VAULT_HALF_WIDTH * 1.9, length),
    new THREE.MeshBasicMaterial({
      color: MALL.daylight,
      transparent: true,
      opacity: 0.07,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  )
  daylightPool.rotation.x = -Math.PI / 2
  daylightPool.position.set(x0, 0.015, center)
  g.add(daylightPool)

  void floor
}

// ── Ceiling: soffits, coves, glazed vault ──────────────────────────────

function buildCeiling(
  g: THREE.Group,
  fin: MallFinishes,
  { x0, center, length, bays }: { x0: number; center: number; length: number; bays: number }
) {
  const soffitW = HW - VAULT_HALF_WIDTH

  // Flat perforated-metal soffit over the shopfronts, either side of the vault
  const soffitMat = new THREE.MeshStandardMaterial({
    map: fin.tiled(fin.soffit, soffitW / 1.2, length / 1.2),
    roughness: 0.75,
    metalness: 0.05,
    side: THREE.DoubleSide,
  })
  for (const s of [-1, 1]) {
    const soffit = new THREE.Mesh(new THREE.PlaneGeometry(soffitW, length), soffitMat)
    soffit.rotation.x = Math.PI / 2
    soffit.position.set(x0 + s * (VAULT_HALF_WIDTH + soffitW / 2), SOFFIT_HEIGHT, center)
    g.add(soffit)
  }

  // Recessed downlight fittings, matched to the pools on the floor
  const fittingMat = new THREE.MeshBasicMaterial({ color: '#fff2dc' })
  const fittingGeo = new THREE.CircleGeometry(0.17, 16)
  const spillMat = new THREE.MeshBasicMaterial({
    map: fin.lightPool,
    color: MALL.warmLight,
    transparent: true,
    opacity: 0.34,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  })
  const spillGeo = new THREE.PlaneGeometry(1.5, 1.5)
  for (let i = 0; i < bays; i++) {
    const z = -CORRIDOR_MARGIN - i * SLOT_WIDTH - SLOT_WIDTH / 2
    for (const s of [-1, 1]) {
      const x = x0 + s * 3.4
      const fitting = new THREE.Mesh(fittingGeo, fittingMat)
      fitting.rotation.x = Math.PI / 2
      fitting.position.set(x, SOFFIT_HEIGHT - 0.012, z)
      g.add(fitting)

      const spill = new THREE.Mesh(spillGeo, spillMat)
      spill.rotation.x = Math.PI / 2
      spill.position.set(x, SOFFIT_HEIGHT - 0.02, z)
      g.add(spill)
    }
  }

  // Cove light in the shadow gap where the soffit meets the vault springing
  const coveMat = new THREE.MeshBasicMaterial({ color: '#fff6e6' })
  const coveGlowMat = new THREE.MeshBasicMaterial({
    map: fin.falloff,
    color: MALL.warmLight,
    transparent: true,
    opacity: 0.4,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  })
  for (const s of [-1, 1]) {
    const x = x0 + s * VAULT_HALF_WIDTH
    const cove = new THREE.Mesh(new THREE.PlaneGeometry(0.16, length), coveMat)
    cove.rotation.x = Math.PI / 2
    cove.position.set(x - s * 0.1, SOFFIT_HEIGHT - 0.015, center)
    g.add(cove)

    // Warm wash climbing the vault above the cove
    const glow = new THREE.Mesh(new THREE.PlaneGeometry(length, 1.5), coveGlowMat)
    glow.rotation.y = s > 0 ? -Math.PI / 2 : Math.PI / 2
    glow.rotation.z = Math.PI // falloff runs upward from the cove
    glow.position.set(x - s * 0.22, SOFFIT_HEIGHT + 0.75, center)
    g.add(glow)
  }

  // Glazed barrel vault. A half-cylinder squashed into an ellipse: 8.4 m span,
  // 2.8 m rise, so the concourse opens up to 7.55 m at the ridge.
  const vaultGeo = new THREE.CylinderGeometry(1, 1, length, 40, 1, true, -Math.PI / 2, Math.PI)
  const vault = new THREE.Mesh(
    vaultGeo,
    new THREE.MeshBasicMaterial({ map: fin.track(makeSkylightTexture(bays + 2)), side: THREE.BackSide })
  )
  vault.scale.set(VAULT_HALF_WIDTH, 1, VAULT_RISE)
  vault.rotation.x = -Math.PI / 2
  vault.position.set(x0, SOFFIT_HEIGHT, center)
  g.add(vault)

  // Structural ribs on the bay lines, reading against the bright glazing
  const ribCurve = new THREE.EllipseCurve(0, 0, VAULT_HALF_WIDTH + 0.06, VAULT_RISE + 0.04, 0, Math.PI, false, 0)
  const ribGeo = new THREE.TubeGeometry(
    new THREE.CatmullRomCurve3(ribCurve.getPoints(28).map((p) => new THREE.Vector3(p.x, p.y, 0))),
    28, 0.055, 6, false
  )
  const ribMat = new THREE.MeshStandardMaterial({ color: MALL.bronze, roughness: 0.45, metalness: 0.7 })
  for (let i = 0; i <= bays + 1; i++) {
    const rib = new THREE.Mesh(ribGeo, ribMat)
    rib.position.set(x0, SOFFIT_HEIGHT, -CORRIDOR_MARGIN - i * SLOT_WIDTH + SLOT_WIDTH)
    g.add(rib)
  }
}

// ── Walls ──────────────────────────────────────────────────────────────

/**
 * Blank stone returns at the two ends of the tenant run — the entrance apron
 * and the end zone. Deliberately *not* drawn across the bays themselves: the
 * shopfronts recess back behind the lease line, so a wall on the lease line
 * would seal every shop interior out of view.
 */
function buildSideWalls(
  g: THREE.Group,
  fin: MallFinishes,
  { x0, bayStart, bayEnd, zFar }: { x0: number; bayStart: number; bayEnd: number; zFar: number }
) {
  const segments: Array<[number, number]> = [
    [ENTRY_ZONE, bayStart], // entrance apron
    [bayEnd, zFar],         // end zone in front of the curtain wall
  ]

  const wallMat = new THREE.MeshStandardMaterial({
    map: fin.tiled(fin.limestone, 3, SOFFIT_HEIGHT / 2.4),
    roughness: 0.8,
    metalness: 0.0,
    side: THREE.DoubleSide,
  })
  const revealMat = new THREE.MeshStandardMaterial({ color: MALL.bronzeLight, roughness: 0.5, metalness: 0.6 })

  for (const [zA, zB] of segments) {
    const len = Math.abs(zA - zB)
    if (len < 0.05) continue
    const mid = (zA + zB) / 2
    for (const s of [-1, 1]) {
      const wall = new THREE.Mesh(new THREE.PlaneGeometry(len, SOFFIT_HEIGHT), wallMat)
      wall.rotation.y = s > 0 ? -Math.PI / 2 : Math.PI / 2
      wall.position.set(x0 + s * HW, SOFFIT_HEIGHT / 2, mid)
      g.add(wall)

      // Bronze reveal at the head of the wall, continuing the tenant fascia line
      const reveal = new THREE.Mesh(new THREE.PlaneGeometry(len, 0.07), revealMat)
      reveal.rotation.y = s > 0 ? -Math.PI / 2 : Math.PI / 2
      reveal.position.set(x0 + s * (HW - 0.02), SOFFIT_HEIGHT - 0.05, mid)
      g.add(reveal)
    }
  }
}

/** Full-height glazing closing the far end, with a lunette filling the vault. */
function buildEndWall(g: THREE.Group, fin: MallFinishes, { x0, zFar }: { x0: number; zFar: number }) {
  const glazing = new THREE.MeshBasicMaterial({
    map: fin.tiled(fin.curtainWall, 2.4, 1),
    side: THREE.DoubleSide,
  })

  const wall = new THREE.Mesh(new THREE.PlaneGeometry(HW * 2, SOFFIT_HEIGHT), glazing)
  wall.position.set(x0, SOFFIT_HEIGHT / 2, zFar)
  g.add(wall)

  // Lunette window in the arch head — a real barrel-vaulted mall always glazes this
  const shape = new THREE.Shape()
  shape.absellipse(0, 0, VAULT_HALF_WIDTH, VAULT_RISE, 0, Math.PI, false, 0)
  const lunette = new THREE.Mesh(
    new THREE.ShapeGeometry(shape, 32),
    new THREE.MeshBasicMaterial({ color: MALL.daylight, side: THREE.DoubleSide })
  )
  lunette.position.set(x0, SOFFIT_HEIGHT, zFar - 0.01)
  g.add(lunette)

  // Radial glazing bars across the lunette
  const barMat = new THREE.MeshBasicMaterial({ color: MALL.bronze, side: THREE.DoubleSide })
  for (let i = 1; i < 6; i++) {
    const a = (i / 6) * Math.PI
    const bar = new THREE.Mesh(new THREE.PlaneGeometry(0.06, VAULT_RISE * 1.02), barMat)
    bar.position.set(
      x0 + Math.cos(a) * VAULT_HALF_WIDTH * 0.5,
      SOFFIT_HEIGHT + (Math.sin(a) * VAULT_RISE) / 2,
      zFar - 0.02
    )
    bar.rotation.z = Math.atan2(Math.sin(a) * VAULT_RISE, Math.cos(a) * VAULT_HALF_WIDTH) - Math.PI / 2
    g.add(bar)
  }

  // Daylight spilling back into the concourse off the end wall
  const spill = new THREE.Mesh(
    new THREE.PlaneGeometry(HW * 2, SOFFIT_HEIGHT + VAULT_RISE),
    new THREE.MeshBasicMaterial({
      color: '#dfeeff',
      transparent: true,
      opacity: 0.1,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    })
  )
  spill.position.set(x0, (SOFFIT_HEIGHT + VAULT_RISE) / 2, zFar + 0.4)
  g.add(spill)
}

/**
 * The entrance to the wing: a glazed wall behind you, a header beam over the
 * concourse mouth, and the category blade sign hung under it. Real centres put
 * the wayfinding gateway exactly here, at the decision point.
 */
function buildEntrance(g: THREE.Group, floor: FloorLayout, fin: MallFinishes) {
  const x0 = floor.originX
  const z = ENTRY_ZONE

  const backWall = new THREE.Mesh(
    new THREE.PlaneGeometry(HW * 2, SOFFIT_HEIGHT + VAULT_RISE),
    new THREE.MeshBasicMaterial({ map: fin.tiled(fin.curtainWall, 2.4, 1.4), side: THREE.DoubleSide })
  )
  backWall.position.set(x0, (SOFFIT_HEIGHT + VAULT_RISE) / 2, z)
  g.add(backWall)

  // Header beam across the concourse mouth
  const beam = new THREE.Mesh(
    new THREE.BoxGeometry(HW * 2, 0.55, 0.5),
    new THREE.MeshStandardMaterial({
      map: fin.tiled(fin.limestone, 6, 0.4),
      roughness: 0.75,
      metalness: 0.05,
    })
  )
  const beamZ = -1.2
  beam.position.set(x0, SOFFIT_HEIGHT - 0.3, beamZ)
  g.add(beam)

  // Suspension rods + the blade sign itself. Sized off a 13 m concourse: big
  // enough to read from the far end, small enough not to be the whole view.
  const rodMat = new THREE.MeshStandardMaterial({ color: MALL.bronze, roughness: 0.4, metalness: 0.8 })
  for (const s of [-1, 1]) {
    const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.42, 6), rodMat)
    rod.position.set(x0 + s * 1.28, SOFFIT_HEIGHT - 0.78, beamZ)
    g.add(rod)
  }

  // Two back-to-back single-sided faces, not one double-sided plane: a
  // double-sided plane shows the same texture from behind, which means mirrored
  // text. A real blade sign is printed on both faces and reads either way.
  const signTex = fin.track(makeGatewaySignTexture(floor.category, floor.color, floor.shopCount))
  const signGeo = new THREE.PlaneGeometry(3.0, 0.75)
  const signMat = new THREE.MeshBasicMaterial({ map: signTex, side: THREE.FrontSide })
  for (const facing of [0, Math.PI]) {
    const sign = new THREE.Mesh(signGeo, signMat)
    sign.rotation.y = facing
    sign.position.set(x0, SOFFIT_HEIGHT - 1.36, beamZ + (facing === 0 ? 0.012 : -0.012))
    g.add(sign)
  }

  buildWingFeaturePanels(g, floor, fin)
}

/**
 * Framed feature panel on each blank apron wall. The stone returns either side
 * of the entrance are the only large flat surfaces in the concourse, and left
 * bare they read as an unfinished box rather than as architecture.
 */
function buildWingFeaturePanels(g: THREE.Group, floor: FloorLayout, fin: MallFinishes) {
  const panelW = 4.4
  const panelH = 2.5
  // Set forward of where you spawn. A panel level with your own shoulder is one
  // you only ever see cropped in half at the edge of frame.
  const zMid = -1.5

  const tex = fin.track(makeWingGraphicTexture(floor.category, floor.color))
  const panelMat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.72, metalness: 0.03 })
  const frameMat = new THREE.MeshStandardMaterial({
    color: MALL.bronzeLight, roughness: 0.35, metalness: 0.85, envMapIntensity: 1.2,
  })
  const frameH = new THREE.BoxGeometry(panelW + 0.16, 0.08, 0.1)
  const frameV = new THREE.BoxGeometry(0.08, panelH + 0.16, 0.1)

  for (const s of [-1, 1]) {
    const x = floor.originX + s * (HW - 0.04)
    const rotY = s > 0 ? -Math.PI / 2 : Math.PI / 2

    const panel = new THREE.Mesh(new THREE.PlaneGeometry(panelW, panelH), panelMat)
    panel.rotation.y = rotY
    panel.position.set(x, 1.9, zMid)
    g.add(panel)

    for (const dy of [-1, 1]) {
      const f = new THREE.Mesh(frameH, frameMat)
      f.rotation.y = rotY
      f.position.set(x - s * 0.02, 1.9 + dy * (panelH / 2 + 0.08), zMid)
      g.add(f)
    }
    for (const dz of [-1, 1]) {
      const f = new THREE.Mesh(frameV, frameMat)
      f.rotation.y = rotY
      f.position.set(x - s * 0.02, 1.9, zMid + dz * (panelW / 2 + 0.08))
      g.add(f)
    }
  }
}
